import { getArchetypes, isTpuLabellingEnabled } from "@/features/branding";
import { downloadCsvFile } from "@/features/custom_downloads";
import { getTranslatedText } from "@/features/internationalisation";
import { ensureGradedRiskValue } from "@/features/risk_bands";
import { anaHazards } from "@/lib/crg_apis/constants/hazards";
import { RiskBandsAdvancedSettingsValues, TpuSubunitById } from "@/tools/risk-bands/types";
import { Rcp, rcpValues } from "@/types/rcp";
import { concatLatLng, limitLatLngTo6dp, postProcessGeocodeQuality } from "@/utils/geocoding";
import { GeocodeWithResult } from "../components/MultipleResultsTable";

/**
 * Triggers a .csv download of a list of assets with Risk Bands data.
 */
export const downloadRiskBandsCsv = ({
  advancedSettings,
  filename,
  results,
  tpuData,
}: {
  advancedSettings: Partial<RiskBandsAdvancedSettingsValues>;
  filename: string;
  results: GeocodeWithResult[];
  tpuData: TpuSubunitById | undefined;
}) => {
  const tpuHeaders = isTpuLabellingEnabled() && tpuData ? ["TPU", "Subunit"] : [];
  const headers = [
    "Provided Address",
    "Geocode Quality",
    "Latitude",
    "Longitude",
    ...tpuHeaders,
    "Build Year",
    "Height Above Ground",
    "Archetype",
    "Scenario",
    "Year",
    "Risk Band",
  ];

  const isWordy =
    results.length && results[0].result && results[0].result.mode ? results[0].result.mode !== "abc" : false;

  if (isWordy) {
    headers.push("Significant Hazard");
  }

  const archetypeLabel = (archetype: string) => getArchetypes().find((a) => a.value === archetype)?.name;

  const body = results.reduce((acc, { geocode, result }) => {
    const currentRcps = Object.keys(result).filter((rcp) => rcpValues.includes(rcp as Rcp));

    const latLng = limitLatLngTo6dp(geocode.latLng);

    const getTpu = () => {
      if (!isTpuLabellingEnabled()) return [];

      const tpuId = concatLatLng(geocode.latLng);
      return tpuData ? [tpuData?.[tpuId]?.tpu || "No Data", tpuData?.[tpuId]?.subunit || "No Data"] : [];
    };

    return [
      ...acc,
      ...currentRcps.flatMap((rcp) => {
        const common = [
          geocode.input,
          postProcessGeocodeQuality({ quality: geocode?.quality, latitude: latLng.lat, longitude: latLng.lng }),
          latLng.lat,
          latLng.lng,
          ...getTpu(),
          advancedSettings.buildYear?.amount,
          advancedSettings.heightAboveGround?.amount,
          archetypeLabel(advancedSettings.archetype || "") || "unknown",
          getTranslatedText(`terms:scenarios.${rcp}.medium`),
        ];

        if (result.mode !== "abc") {
          return result[rcp as "rcp85"].ratings.map((rating) => {
            const hazards =
              result[rcp as "rcp85"].top_significant_hazards
                .map((h) => getTranslatedText(`terms:hazards.${anaHazards[h].newId}`))
                .join(", ") || "None detected";

            return [...common, rating.year, ensureGradedRiskValue(rating.rating!), hazards];
          });
        } else {
          const { current_year, end_of_century, end_of_mortgage_term } = result.rcp85;
          return [
            [...common, current_year.year, ensureGradedRiskValue(current_year.rating!)],
            [...common, end_of_mortgage_term.year, ensureGradedRiskValue(end_of_mortgage_term.rating!)],
            [...common, end_of_century.year, ensureGradedRiskValue(end_of_century.rating!)],
          ];
        }
      }),
    ];
  }, [] as any);

  downloadCsvFile([headers, ...body], {
    filename,
    prefixDate: true,
  });
};
